import { useEffect, useState } from "react";
import {
  CreditCard,
  Loader2,
  ShieldCheck,
  Clock3,
} from "lucide-react";
import { toast } from "sonner";

import { api, apiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Checkbox } from "../../components/ui/checkbox";


export default function AdminPaymentProviders() {
  const [providers, setProviders] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    loadProviders();
  }, []);

  const loadProviders = async () => {
    setLoading(true);
    setError("");

    try {
      const { data } = await api.get(
        "/admin/payment-providers"
      );

      setProviders(data.providers || []);
      setSelected(data.enabled_providers || []);
    } catch (e) {
      setError(
        apiError(
          e,
          "Failed to load payment providers"
        )
      );
    } finally {
      setLoading(false);
    }
  };

  const toggleProvider = (provider, checked) => {
    if (!provider.implemented) {
      toast.info(
        `${provider.name} integration is coming soon`
      );
      return;
    }

    setSelected((current) => {
      if (checked) {
        return [...new Set([...current, provider.key])];
      }

      return current.filter(
        (key) => key !== provider.key
      );
    });
  };

  const save = async () => {
    setSaving(true);
    setError("");

    try {
      const { data } = await api.patch(
        "/admin/payment-providers",
        {
          enabled_providers: selected,
        }
      );

      setProviders(data.providers || []);
      setSelected(data.enabled_providers || []);

      toast.success(
        "Payment provider settings updated"
      );
    } catch (e) {
      setError(
        apiError(
          e,
          "Failed to save payment providers"
        )
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-mb-fg2">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading payment providers…
      </div>
    );
  }

  return (
    <div
      className="space-y-6 max-w-4xl"
      data-testid="admin-payment-providers"
    >
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-teal-400 inline-flex items-center gap-2">
          <CreditCard className="h-3 w-3" />
          Payment Providers
        </div>

        <h1 className="mt-2 text-3xl font-light tracking-tight">
          Payment Provider Control
        </h1>

        <p className="mt-2 text-sm text-mb-fg2 max-w-2xl leading-6">
          Choose which payment gateways are available across
          MediBooks. Hospital administrators can only configure
          providers enabled here.
        </p>
      </div>

      <div className="rounded-xl border border-mb-border bg-mb-surface overflow-hidden">
        {providers.map((provider, index) => {
          const checked = selected.includes(
            provider.key
          );

          return (
            <div
              key={provider.key}
              className={`p-5 flex items-start justify-between gap-5 ${
                index !== providers.length - 1
                  ? "border-b border-mb-border"
                  : ""
              }`}
            >
              <div className="flex items-start gap-4">
                <Checkbox
                  checked={checked}
                  disabled={!provider.implemented}
                  onCheckedChange={(value) =>
                    toggleProvider(
                      provider,
                      value === true
                    )
                  }
                  className="mt-1"
                  data-testid={`provider-${provider.key}`}
                />

                <div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-white">
                      {provider.name}
                    </span>

                    {provider.implemented ? (
                      <span className="inline-flex items-center gap-1 rounded border border-teal-500/20 bg-teal-500/10 px-2 py-0.5 text-[9px] uppercase tracking-[0.15em] text-teal-300">
                        <ShieldCheck className="h-3 w-3" />
                        Available
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded border border-amber-500/20 bg-amber-500/10 px-2 py-0.5 text-[9px] uppercase tracking-[0.15em] text-amber-300">
                        <Clock3 className="h-3 w-3" />
                        Coming soon
                      </span>
                    )}
                  </div>

                  <div className="mt-1 text-xs text-mb-fg2">
                    {provider.description}
                  </div>
                </div>
              </div>

              <div
                className={`text-[10px] uppercase tracking-[0.18em] ${
                  checked
                    ? "text-teal-400"
                    : "text-mb-fg2"
                }`}
              >
                {checked ? "Enabled" : "Disabled"}
              </div>
            </div>
          );
        })}
      </div>

      <div className="rounded-lg border border-mb-border bg-mb-surface2 p-4 text-xs text-mb-fg2 leading-5">
        Enabling a provider here does not enable payments for every
        hospital automatically. Each hospital still requires the
        Super Admin Payment Feature switch to be enabled, and the
        Hospital Admin must configure its own gateway credentials.
      </div>

      {error && (
        <div className="rounded-lg border border-red-500/20 bg-red-500/10 p-3 text-sm text-red-400">
          {error}
        </div>
      )}

      <Button
        onClick={save}
        disabled={saving}
        className="bg-teal-500 hover:bg-teal-400 text-zinc-950"
      >
        {saving ? (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            Saving…
          </>
        ) : (
          "Save provider settings"
        )}
      </Button>
    </div>
  );
}
